import React, { useState, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosGet, axiosPost, axiosDelete } from "@/services/axiosService";
import "./Integrations.css";

interface ConnectedService {
  _id: string;
  type: string;
  name: string;
  configName?: string;
  email?: string;
  accountId?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// AI providers and common services that users can connect
const AVAILABLE_SERVICES: Array<{
  type: string;
  displayName: string;
  description: string;
  icon: string;
  color: string;
  fields: Array<{
    name: string;
    label: string;
    placeholder: string;
    type?: string;
  }>;
}> = [
  {
    type: "openai",
    displayName: "OpenAI",
    description: "GPT-4o, GPT-4o-mini, DALL-E, Whisper",
    icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/4d/OpenAI_Logo.svg/120px-OpenAI_Logo.svg.png",
    color: "#10a37f",
    fields: [{ name: "accessToken", label: "API Key", placeholder: "sk-..." }],
  },
  {
    type: "anthropic",
    displayName: "Anthropic",
    description: "Claude Opus, Sonnet, Haiku models",
    icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/78/Anthropic_logo.svg/120px-Anthropic_logo.svg.png",
    color: "#d97706",
    fields: [
      { name: "accessToken", label: "API Key", placeholder: "sk-ant-..." },
    ],
  },
  {
    type: "openrouter",
    displayName: "OpenRouter",
    description: "Access 100+ AI models with one API key",
    icon: "",
    color: "#6366f1",
    fields: [
      { name: "accessToken", label: "API Key", placeholder: "sk-or-..." },
    ],
  },
  {
    type: "groq",
    displayName: "Groq",
    description: "Ultra-fast inference for LLaMA, Mixtral",
    icon: "",
    color: "#f55036",
    fields: [{ name: "accessToken", label: "API Key", placeholder: "gsk_..." }],
  },
  {
    type: "sendgrid",
    displayName: "SendGrid",
    description: "Email sending and delivery tracking",
    icon: "",
    color: "#1a82e2",
    fields: [{ name: "accessToken", label: "API Key", placeholder: "SG...." }],
  },
  {
    type: "twilio",
    displayName: "Twilio",
    description: "SMS, voice calls, and phone verification",
    icon: "",
    color: "#f22f46",
    fields: [
      { name: "accountSid", label: "Account SID", placeholder: "AC..." },
      {
        name: "accessToken",
        label: "Auth Token",
        placeholder: "Your auth token",
      },
    ],
  },
  {
    type: "stripe",
    displayName: "Stripe",
    description: "Payment processing and billing",
    icon: "",
    color: "#635bff",
    fields: [
      { name: "accessToken", label: "Secret Key", placeholder: "sk_live_..." },
    ],
  },
  {
    type: "hubspot",
    displayName: "HubSpot",
    description: "CRM, contacts, deals, and marketing",
    icon: "",
    color: "#ff7a59",
    fields: [
      {
        name: "accessToken",
        label: "API Key / Access Token",
        placeholder: "pat-...",
      },
    ],
  },
  {
    type: "slack",
    displayName: "Slack",
    description: "Messaging and notifications",
    icon: "",
    color: "#4a154b",
    fields: [
      { name: "accessToken", label: "Bot Token", placeholder: "xoxb-..." },
    ],
  },
  {
    type: "google",
    displayName: "Google (Gmail / Sheets)",
    description: "Gmail sending, Google Sheets, Calendar",
    icon: "",
    color: "#4285f4",
    fields: [
      {
        name: "accessToken",
        label: "API Key / OAuth Token",
        placeholder: "Your API key",
      },
    ],
  },
  {
    type: "mistral",
    displayName: "Mistral AI",
    description: "Mistral Large, Medium, Small models",
    icon: "",
    color: "#ff7000",
    fields: [
      {
        name: "accessToken",
        label: "API Key",
        placeholder: "Your Mistral API key",
      },
    ],
  },
  {
    type: "perplexity",
    displayName: "Perplexity",
    description: "AI search with real-time web data",
    icon: "",
    color: "#20808d",
    fields: [
      { name: "accessToken", label: "API Key", placeholder: "pplx-..." },
    ],
  },
  // --- CRM & Sales ---
  {
    type: "salesforce",
    displayName: "Salesforce",
    description: "CRM, leads, contacts, opportunities",
    icon: "",
    color: "#00a1e0",
    fields: [
      {
        name: "accessToken",
        label: "Access Token",
        placeholder: "Your Salesforce token",
      },
    ],
  },
  {
    type: "pipedrive",
    displayName: "Pipedrive",
    description: "Sales pipeline and deal management",
    icon: "",
    color: "#25292c",
    fields: [
      {
        name: "accessToken",
        label: "API Token",
        placeholder: "Your Pipedrive token",
      },
    ],
  },
  {
    type: "zoho_crm",
    displayName: "Zoho CRM",
    description: "CRM, contacts, and automation",
    icon: "",
    color: "#e42527",
    fields: [
      { name: "accessToken", label: "API Key", placeholder: "Your Zoho key" },
    ],
  },
  // --- Communication ---
  {
    type: "discord",
    displayName: "Discord",
    description: "Bot messages and webhook notifications",
    icon: "",
    color: "#5865f2",
    fields: [
      {
        name: "accessToken",
        label: "Bot Token / Webhook URL",
        placeholder: "Your Discord token",
      },
    ],
  },
  {
    type: "telegram",
    displayName: "Telegram",
    description: "Bot messages and notifications",
    icon: "",
    color: "#0088cc",
    fields: [
      {
        name: "accessToken",
        label: "Bot Token",
        placeholder: "Your bot token from @BotFather",
      },
    ],
  },
  {
    type: "microsoft_teams",
    displayName: "Microsoft Teams",
    description: "Team messaging and notifications",
    icon: "",
    color: "#6264a7",
    fields: [
      { name: "accessToken", label: "Webhook URL", placeholder: "https://..." },
    ],
  },
  // --- E-Commerce ---
  {
    type: "shopify",
    displayName: "Shopify",
    description: "Orders, products, customers, inventory",
    icon: "",
    color: "#96bf48",
    fields: [
      {
        name: "accessToken",
        label: "Admin API Access Token",
        placeholder: "shpat_...",
      },
      {
        name: "storeUrl",
        label: "Store URL",
        placeholder: "yourstore.myshopify.com",
      },
    ],
  },
  {
    type: "woocommerce",
    displayName: "WooCommerce",
    description: "WordPress e-commerce orders and products",
    icon: "",
    color: "#96588a",
    fields: [
      { name: "consumerKey", label: "Consumer Key", placeholder: "ck_..." },
      {
        name: "consumerSecret",
        label: "Consumer Secret",
        placeholder: "cs_...",
      },
      { name: "storeUrl", label: "Store URL", placeholder: "yourstore.com" },
    ],
  },
  // --- Project Management ---
  {
    type: "jira",
    displayName: "Jira",
    description: "Issue tracking and project management",
    icon: "",
    color: "#0052cc",
    fields: [
      {
        name: "accessToken",
        label: "API Token",
        placeholder: "Your Jira API token",
      },
      {
        name: "instanceUrl",
        label: "Domain",
        placeholder: "yourteam.atlassian.net",
      },
    ],
  },
  {
    type: "asana",
    displayName: "Asana",
    description: "Task and project management",
    icon: "",
    color: "#f06a6a",
    fields: [
      {
        name: "accessToken",
        label: "Personal Access Token",
        placeholder: "Your Asana token",
      },
    ],
  },
  {
    type: "trello",
    displayName: "Trello",
    description: "Boards, lists, and cards",
    icon: "",
    color: "#0079bf",
    fields: [
      { name: "accessToken", label: "API Key", placeholder: "Your Trello key" },
      { name: "apiToken", label: "Token", placeholder: "Your Trello token" },
    ],
  },
  {
    type: "linear",
    displayName: "Linear",
    description: "Issue tracking for engineering teams",
    icon: "",
    color: "#5e6ad2",
    fields: [
      { name: "accessToken", label: "API Key", placeholder: "lin_api_..." },
    ],
  },
  {
    type: "notion",
    displayName: "Notion",
    description: "Docs, databases, and wikis",
    icon: "",
    color: "#000000",
    fields: [
      {
        name: "accessToken",
        label: "Integration Token",
        placeholder: "secret_...",
      },
    ],
  },
  // --- Data & Storage ---
  {
    type: "airtable",
    displayName: "Airtable",
    description: "Spreadsheet-database hybrid",
    icon: "",
    color: "#18bfff",
    fields: [{ name: "accessToken", label: "API Key", placeholder: "pat..." }],
  },
  {
    type: "google_sheets",
    displayName: "Google Sheets",
    description: "Spreadsheet read/write",
    icon: "",
    color: "#0f9d58",
    fields: [
      {
        name: "accessToken",
        label: "API Key / Service Account",
        placeholder: "Your key",
      },
    ],
  },
  {
    type: "google_drive",
    displayName: "Google Drive",
    description: "File storage and sharing",
    icon: "",
    color: "#4285f4",
    fields: [
      { name: "accessToken", label: "OAuth Token", placeholder: "Your token" },
    ],
  },
  {
    type: "google_analytics",
    displayName: "Google Analytics",
    description: "Website traffic and user analytics",
    icon: "",
    color: "#e37400",
    fields: [
      {
        name: "accessToken",
        label: "API Key / Service Account",
        placeholder: "Your key",
      },
    ],
  },
  {
    type: "google_calendar",
    displayName: "Google Calendar",
    description: "Events and scheduling",
    icon: "",
    color: "#4285f4",
    fields: [
      { name: "accessToken", label: "OAuth Token", placeholder: "Your token" },
    ],
  },
  // --- Email ---
  {
    type: "mailchimp",
    displayName: "Mailchimp",
    description: "Email marketing and newsletters",
    icon: "",
    color: "#ffe01b",
    fields: [
      {
        name: "accessToken",
        label: "API Key",
        placeholder: "Your Mailchimp key",
      },
    ],
  },
  {
    type: "mailgun",
    displayName: "Mailgun",
    description: "Transactional email delivery",
    icon: "",
    color: "#f06b54",
    fields: [
      {
        name: "accessToken",
        label: "API Key",
        placeholder: "Your Mailgun key",
      },
    ],
  },
  {
    type: "postmark",
    displayName: "Postmark",
    description: "Reliable transactional email",
    icon: "",
    color: "#ffde00",
    fields: [
      {
        name: "accessToken",
        label: "Server API Token",
        placeholder: "Your Postmark token",
      },
    ],
  },
  // --- Developer ---
  {
    type: "github",
    displayName: "GitHub",
    description: "Repos, issues, PRs, and CI/CD",
    icon: "",
    color: "#24292e",
    fields: [
      {
        name: "accessToken",
        label: "Personal Access Token",
        placeholder: "ghp_...",
      },
    ],
  },
  {
    type: "gitlab",
    displayName: "GitLab",
    description: "Git repos and DevOps pipelines",
    icon: "",
    color: "#fc6d26",
    fields: [
      {
        name: "accessToken",
        label: "Personal Access Token",
        placeholder: "glpat-...",
      },
    ],
  },
  // --- Support ---
  {
    type: "zendesk",
    displayName: "Zendesk",
    description: "Customer support tickets",
    icon: "",
    color: "#03363d",
    fields: [
      {
        name: "accessToken",
        label: "API Token",
        placeholder: "Your Zendesk token",
      },
      {
        name: "instanceUrl",
        label: "Subdomain",
        placeholder: "yourcompany.zendesk.com",
      },
    ],
  },
  {
    type: "intercom",
    displayName: "Intercom",
    description: "Customer messaging and support",
    icon: "",
    color: "#286efa",
    fields: [
      {
        name: "accessToken",
        label: "Access Token",
        placeholder: "Your Intercom token",
      },
    ],
  },
  {
    type: "freshdesk",
    displayName: "Freshdesk",
    description: "Help desk and ticketing",
    icon: "",
    color: "#25c16f",
    fields: [
      {
        name: "accessToken",
        label: "API Key",
        placeholder: "Your Freshdesk key",
      },
      {
        name: "instanceUrl",
        label: "Domain",
        placeholder: "yourcompany.freshdesk.com",
      },
    ],
  },
  // --- Video & Media ---
  {
    type: "riverside",
    displayName: "Riverside",
    description: "Podcast and video recording",
    icon: "",
    color: "#6c63ff",
    fields: [
      {
        name: "accessToken",
        label: "API Key",
        placeholder: "Your Riverside key",
      },
    ],
  },
  {
    type: "youtube",
    displayName: "YouTube",
    description: "Video upload and management",
    icon: "",
    color: "#ff0000",
    fields: [
      {
        name: "accessToken",
        label: "OAuth Token",
        placeholder: "Your YouTube token",
      },
    ],
  },
  // --- Analytics ---
  {
    type: "segment",
    displayName: "Segment",
    description: "Customer data platform",
    icon: "",
    color: "#52bd94",
    fields: [
      {
        name: "accessToken",
        label: "Write Key",
        placeholder: "Your Segment key",
      },
    ],
  },
  {
    type: "mixpanel",
    displayName: "Mixpanel",
    description: "Product analytics and events",
    icon: "",
    color: "#7856ff",
    fields: [
      {
        name: "accessToken",
        label: "Project Token",
        placeholder: "Your Mixpanel token",
      },
    ],
  },
  // --- Finance ---
  {
    type: "quickbooks",
    displayName: "QuickBooks",
    description: "Accounting and invoicing",
    icon: "",
    color: "#2ca01c",
    fields: [
      {
        name: "accessToken",
        label: "OAuth Token",
        placeholder: "Your QuickBooks token",
      },
    ],
  },
  {
    type: "xero",
    displayName: "Xero",
    description: "Cloud accounting software",
    icon: "",
    color: "#13b5ea",
    fields: [
      {
        name: "accessToken",
        label: "OAuth Token",
        placeholder: "Your Xero token",
      },
    ],
  },
];

export default function Integrations() {
  const [searchQuery, setSearchQuery] = useState("");
  const [connectModal, setConnectModal] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState("");
  const [connectSuccess, setConnectSuccess] = useState("");
  const queryClient = useQueryClient();

  // Fetch connected services with detail
  const { data: connectedServices = [], isLoading } = useQuery<
    ConnectedService[]
  >({
    queryKey: ["connectedServices"],
    queryFn: async () => {
      const res = await axiosGet("/integration/connected-services?detail=true");
      return res.data?.data || [];
    },
  });

  const connectedTypes = new Set(connectedServices.map((s) => s.type));

  const handleConnect = useCallback(
    async (serviceType: string) => {
      const service = AVAILABLE_SERVICES.find((s) => s.type === serviceType);
      if (!service) return;

      // Check required fields
      const missingField = service.fields.find(
        (f) => !formValues[f.name]?.trim(),
      );
      if (missingField) {
        setConnectError(`${missingField.label} is required`);
        return;
      }

      setConnecting(true);
      setConnectError("");
      setConnectSuccess("");

      try {
        await axiosPost(`/integration/${serviceType}`, {
          credentials: formValues,
          configName: `${service.displayName} (from integrations)`,
        });
        setConnectSuccess(`${service.displayName} connected successfully!`);
        setFormValues({});
        queryClient.invalidateQueries({ queryKey: ["connectedServices"] });
        setTimeout(() => {
          setConnectModal(null);
          setConnectSuccess("");
        }, 1500);
      } catch (err: any) {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Connection failed. Check your API key.";
        setConnectError(msg);
      } finally {
        setConnecting(false);
      }
    },
    [formValues, queryClient],
  );

  const handleDisconnect = useCallback(
    async (service: ConnectedService) => {
      if (
        !window.confirm(
          `Remove ${service.type} connection? You can reconnect later.`,
        )
      )
        return;
      try {
        await axiosDelete(`/integration/${service.type}/${service._id}`);
        queryClient.invalidateQueries({ queryKey: ["connectedServices"] });
      } catch {
        alert("Failed to remove connection. Please try again.");
      }
    },
    [queryClient],
  );

  const openConnectModal = (type: string) => {
    setFormValues({});
    setConnectError("");
    setConnectSuccess("");
    setConnectModal(type);
  };

  // Fetch ALL available platforms from backend (2600+ platform configs)
  const { data: allPlatforms = [] } = useQuery<
    Array<{ id: string; name: string; category: string }>
  >({
    queryKey: ["allPlatforms"],
    queryFn: async () => {
      try {
        const res = await axiosGet("/integration/available-platforms");
        return res.data?.data || [];
      } catch {
        return [];
      }
    },
    staleTime: 600000, // Cache for 10 min
  });

  // Build complete list: curated popular services + connected services + search-matching platforms
  const knownTypes = new Set(AVAILABLE_SERVICES.map((s) => s.type));

  // Add dynamically-connected services not in the curated list
  const dynamicServices = connectedServices
    .filter((s) => !knownTypes.has(s.type))
    .map((s) => ({
      type: s.type,
      displayName:
        s.name ||
        s.type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      description: `Connected via workflow chat`,
      icon: "",
      color: "#6b7280",
      fields: [
        { name: "accessToken", label: "API Key", placeholder: "Your API key" },
      ],
    }));

  // When searching, also include matching platforms from the full 2600+ list
  const extraPlatforms =
    searchQuery.trim().length >= 2
      ? allPlatforms
          .filter(
            (p) =>
              !knownTypes.has(p.id) &&
              !dynamicServices.some((d) => d.type === p.id),
          )
          .filter((p) => {
            const q = searchQuery.toLowerCase();
            return (
              p.name.toLowerCase().includes(q) ||
              p.id.toLowerCase().includes(q) ||
              (p.category || "").toLowerCase().includes(q)
            );
          })
          .slice(0, 50) // Show max 50 search results from extended list
          .map((p) => ({
            type: p.id,
            displayName: p.name,
            description: `${(p.category || "other").replace(/_/g, " ")} integration`,
            icon: "",
            color: "#6b7280",
            fields: [
              {
                name: "accessToken",
                label: "API Key",
                placeholder: "Your API key",
              },
            ],
          }))
      : [];

  const allServices = [
    ...AVAILABLE_SERVICES,
    ...dynamicServices,
    ...extraPlatforms,
  ];

  const filteredServices = allServices.filter(
    (s) =>
      !searchQuery.trim() ||
      s.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.type.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const getInitial = (name: string) => name.charAt(0).toUpperCase();
  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  return (
    <div className="integration-dashboard">
      <div className="neurodes-content">
        <div className="neurodes-header">
          <h1>Integrations</h1>
          <p style={{ color: "#666", marginTop: 4, fontSize: 14 }}>
            Connect your AI providers, email services, CRMs, and 2,600+ more.
            Search to find any service.
          </p>
        </div>

        {/* Connected Services Section */}
        {connectedServices.length > 0 && (
          <div style={{ marginBottom: 32 }}>
            <h2
              style={{
                fontSize: 16,
                fontWeight: 500,
                color: "#333",
                marginBottom: 16,
                fontFamily: "Rubik Medium",
              }}
            >
              Connected ({connectedServices.length})
            </h2>
            <div className="integrations-grid">
              {connectedServices.map((svc) => {
                const meta = AVAILABLE_SERVICES.find(
                  (s) => s.type === svc.type,
                );
                const color = meta?.color || "#666";
                return (
                  <div
                    key={svc._id}
                    className="integration-card"
                    style={{ borderTop: `3px solid ${color}` }}
                  >
                    <div
                      className="integration-card-header"
                      style={{ background: `${color}10`, padding: "20px" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          width: "100%",
                        }}
                      >
                        {meta?.icon ? (
                          <img
                            src={meta.icon}
                            alt={meta.displayName}
                            style={{
                              width: 28,
                              height: 28,
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: 8,
                              background: color,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            {getInitial(meta?.displayName || svc.type)}
                          </div>
                        )}
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: 15,
                              color: "#333",
                            }}
                          >
                            {meta?.displayName || svc.type}
                          </div>
                          <div style={{ fontSize: 12, color: "#888" }}>
                            Connected {formatDate(svc.createdAt)}
                          </div>
                        </div>
                        <div
                          style={{
                            background: svc.isActive ? "#dcfce7" : "#fee2e2",
                            color: svc.isActive ? "#166534" : "#991b1b",
                            fontSize: 11,
                            fontWeight: 600,
                            padding: "3px 8px",
                            borderRadius: 12,
                          }}
                        >
                          {svc.isActive ? "Active" : "Inactive"}
                        </div>
                      </div>
                    </div>
                    <div
                      className="integration-card-content"
                      style={{ padding: "16px 20px" }}
                    >
                      <p
                        style={{
                          fontSize: 13,
                          color: "#666",
                          margin: "0 0 12px 0",
                        }}
                      >
                        {meta?.description || `${svc.type} integration`}
                      </p>
                      <div
                        style={{ display: "flex", gap: 8, marginTop: "auto" }}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openConnectModal(svc.type);
                          }}
                          style={{
                            flex: 1,
                            padding: "8px 12px",
                            border: "1px solid #e0e0e0",
                            borderRadius: 6,
                            background: "#fff",
                            color: "#333",
                            fontSize: 13,
                            cursor: "pointer",
                            fontFamily: "Rubik Medium",
                          }}
                        >
                          Replace Key
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDisconnect(svc);
                          }}
                          style={{
                            padding: "8px 12px",
                            border: "1px solid #fee2e2",
                            borderRadius: 6,
                            background: "#fff",
                            color: "#dc2626",
                            fontSize: 13,
                            cursor: "pointer",
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="search-box-container">
          <h2
            style={{
              fontSize: 16,
              fontWeight: 500,
              color: "#333",
              marginBottom: 12,
              fontFamily: "Rubik Medium",
            }}
          >
            Available Integrations
          </h2>
          <input
            type="text"
            placeholder="Search integrations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        {/* Available Services Grid */}
        <div className="integrations-grid">
          {isLoading ? (
            <div className="loading">Loading integrations...</div>
          ) : (
            filteredServices.map((service) => {
              const isConnected = connectedTypes.has(service.type);
              return (
                <div
                  key={service.type}
                  className="integration-card"
                  onClick={() => openConnectModal(service.type)}
                  style={{ opacity: isConnected ? 0.7 : 1 }}
                >
                  <div
                    className="integration-card-header"
                    style={{ background: `${service.color}08` }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 12 }}
                    >
                      {service.icon ? (
                        <img
                          src={service.icon}
                          alt={service.displayName}
                          style={{
                            width: 28,
                            height: 28,
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 8,
                            background: service.color,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontWeight: 600,
                            fontSize: 16,
                          }}
                        >
                          {getInitial(service.displayName)}
                        </div>
                      )}
                      <span
                        style={{ fontWeight: 600, fontSize: 15, color: "#333" }}
                      >
                        {service.displayName}
                      </span>
                      {isConnected && (
                        <span
                          style={{
                            background: "#dcfce7",
                            color: "#166534",
                            fontSize: 11,
                            fontWeight: 600,
                            padding: "2px 8px",
                            borderRadius: 12,
                            marginLeft: "auto",
                          }}
                        >
                          Connected
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="integration-card-content">
                    <p
                      className="integration-description"
                      style={{ margin: "0 0 12px 0" }}
                    >
                      {service.description}
                    </p>
                    <div className="configure-button">
                      {isConnected ? "Update Key" : "Connect"}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Connect Modal */}
      {connectModal &&
        (() => {
          const service = AVAILABLE_SERVICES.find(
            (s) => s.type === connectModal,
          );
          if (!service) return null;
          const isAlreadyConnected = connectedTypes.has(connectModal);
          return (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0,0,0,0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 9999,
              }}
              onClick={() => setConnectModal(null)}
            >
              <div
                style={{
                  background: "#fff",
                  borderRadius: 12,
                  padding: 32,
                  width: 440,
                  maxWidth: "90vw",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 20,
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      background: service.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 18,
                    }}
                  >
                    {service.icon ? (
                      <img
                        src={service.icon}
                        alt=""
                        style={{ width: 24, height: 24, objectFit: "contain" }}
                      />
                    ) : (
                      getInitial(service.displayName)
                    )}
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                      {isAlreadyConnected
                        ? `Update ${service.displayName}`
                        : `Connect ${service.displayName}`}
                    </h3>
                    <p style={{ margin: 0, fontSize: 13, color: "#666" }}>
                      {service.description}
                    </p>
                  </div>
                </div>

                {isAlreadyConnected && (
                  <div
                    style={{
                      background: "#fff7ed",
                      border: "1px solid #fed7aa",
                      borderRadius: 8,
                      padding: "10px 14px",
                      marginBottom: 16,
                      fontSize: 13,
                      color: "#9a3412",
                    }}
                  >
                    This will replace your existing {service.displayName} API
                    key.
                  </div>
                )}

                {service.fields.map((field) => (
                  <div key={field.name} style={{ marginBottom: 16 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 500,
                        color: "#333",
                        marginBottom: 6,
                      }}
                    >
                      {field.label}
                    </label>
                    <input
                      type={field.type || "password"}
                      placeholder={field.placeholder}
                      value={formValues[field.name] || ""}
                      onChange={(e) =>
                        setFormValues((prev) => ({
                          ...prev,
                          [field.name]: e.target.value,
                        }))
                      }
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleConnect(connectModal)
                      }
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        border: "1px solid #e0e0e0",
                        borderRadius: 8,
                        fontSize: 14,
                        boxSizing: "border-box",
                        outline: "none",
                        transition: "border-color 0.2s",
                      }}
                      autoFocus={service.fields.indexOf(field) === 0}
                    />
                  </div>
                ))}

                {connectError && (
                  <div
                    style={{
                      color: "#dc2626",
                      fontSize: 13,
                      marginBottom: 12,
                      padding: "8px 12px",
                      background: "#fef2f2",
                      borderRadius: 6,
                    }}
                  >
                    {connectError}
                  </div>
                )}
                {connectSuccess && (
                  <div
                    style={{
                      color: "#166534",
                      fontSize: 13,
                      marginBottom: 12,
                      padding: "8px 12px",
                      background: "#f0fdf4",
                      borderRadius: 6,
                    }}
                  >
                    {connectSuccess}
                  </div>
                )}

                <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                  <button
                    onClick={() => setConnectModal(null)}
                    style={{
                      flex: 1,
                      padding: "10px 16px",
                      border: "1px solid #e0e0e0",
                      borderRadius: 8,
                      background: "#fff",
                      color: "#333",
                      fontSize: 14,
                      cursor: "pointer",
                      fontFamily: "Rubik Medium",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleConnect(connectModal)}
                    disabled={connecting}
                    style={{
                      flex: 1,
                      padding: "10px 16px",
                      border: "none",
                      borderRadius: 8,
                      background: connecting ? "#ccc" : service.color,
                      color: "#fff",
                      fontSize: 14,
                      cursor: connecting ? "default" : "pointer",
                      fontFamily: "Rubik Medium",
                    }}
                  >
                    {connecting
                      ? "Connecting..."
                      : isAlreadyConnected
                        ? "Update Key"
                        : "Connect"}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
}
